// Bolo3K touch controls (Wave 2b-1) — the mobile InputSource. Mounts a
// floating-origin virtual stick (bottom-left), a hold-to-fire button with
// vertical-drag gunsight range (bottom-right), and a tap-to-drop mine
// button into #controls-root, then registers itself via
// ctx.setInputSource().
//
// Steering translation lives in steering.ts (pure, unit-tested); this file
// owns the pointer machinery, DOM, and per-frame composition in readKeys().
//
// Multi-touch: every control tracks its own pointerId and uses
// setPointerCapture, so a finger on the fire button never disturbs the
// stick and vice versa.

import { TankKey, type BuildCommand } from '@bolo2k/sim';
import type { GameContext } from '../context.js';
import type { InputSource } from './inputSource.js';
import { drive, shortestDelta, stickToDir, type StickVector } from './steering.js';
import './touchControls.css';

/** setPointerCapture throws NotFoundError when the pointer is no longer
 *  active (released between dispatch and capture, some stylus/assistive
 *  pointers, synthetic events). Capture is an optimization — drag tracking
 *  works uncaptured via the zone's own move/up listeners — so never let it
 *  kill the handler. */
function capturePointer(el: HTMLElement, pointerId: number): void {
  try {
    el.setPointerCapture(pointerId);
  } catch {
    // Uncaptured drags still work; capture only guards against the finger
    // sliding off the element mid-gesture.
  }
}

/** Pixels of drag for full stick deflection (magnitude 1). Larger travel =
 *  finer angular control and a comfortable aim/drive ring split (the drive
 *  ring engages at ~0.65 magnitude ≈ 47px; see steering.ts). Keep in sync
 *  with the dial art in touchControls.css (dial 160px, ring marker). */
const STICK_RADIUS_PX = 72;
/** Vertical drag (px) on the held fire button before range stepping engages.
 *  The sim rate-limits actual steps via GUNSIGHT_STEP_TICKS; we just hold
 *  the MoreRange/LessRange bit while displaced beyond this threshold. */
const RANGE_DRAG_PX = 28;

export interface TouchControlsApi {
  /** Remove all DOM and listeners. Equivalent to InputSource.dispose();
   *  main.ts also calls dispose() if another source replaces this one. */
  dispose(): void;
  /** The controls layer element (exposed for tests/debugging). */
  element: HTMLElement;
}

export function initTouchControls(ctx: GameContext, root: HTMLElement): TouchControlsApi {
  // ---------- DOM ----------
  const layer = document.createElement('div');
  layer.className = 'tc-layer';

  const steerZone = document.createElement('div');
  steerZone.className = 'tc-steer-zone';

  const stickBase = document.createElement('div');
  stickBase.className = 'tc-stick-base';
  const stickNub = document.createElement('div');
  stickNub.className = 'tc-stick-nub';
  stickBase.appendChild(stickNub);

  const fireBtn = document.createElement('div');
  fireBtn.className = 'tc-btn tc-fire';
  const fireLabel = document.createElement('span');
  fireLabel.className = 'tc-btn-label';
  fireLabel.textContent = 'FIRE';
  const rangeLabel = document.createElement('span');
  rangeLabel.className = 'tc-range-label';
  rangeLabel.textContent = '';
  const rangeScale = document.createElement('span');
  rangeScale.className = 'tc-range-scale'; // decorative gunsight ruler (CSS-only art)
  fireBtn.append(fireLabel, rangeLabel, rangeScale);

  const mineBtn = document.createElement('div');
  mineBtn.className = 'tc-btn tc-mine';
  const mineLabel = document.createElement('span');
  mineLabel.className = 'tc-btn-label';
  mineLabel.textContent = 'MINE';
  mineBtn.appendChild(mineLabel);

  // The dial lives inside its circular hit zone (fixed instrument — the
  // zone's center is the stick origin; see the GEOMETRY CONTRACT in the CSS).
  steerZone.appendChild(stickBase);
  layer.append(steerZone, fireBtn, mineBtn);
  root.appendChild(layer);

  // ---------- Stick state (floating origin) ----------
  let stickPointerId: number | null = null;
  let stickOriginX = 0;
  let stickOriginY = 0;
  const stick: StickVector = { x: 0, y: 0 };

  const resetStick = (): void => {
    stickPointerId = null;
    stick.x = 0;
    stick.y = 0;
    stickBase.classList.remove('tc-active', 'tc-reversing');
    stickNub.style.transform = '';
  };

  const onSteerDown = (e: PointerEvent): void => {
    if (stickPointerId !== null) return; // one finger drives; ignore extras
    stickPointerId = e.pointerId;
    capturePointer(steerZone, e.pointerId);
    // Fixed instrument: the origin is the dial's center, not the touch
    // point — a thumb landing off-center deflects immediately.
    const zr = steerZone.getBoundingClientRect();
    stickOriginX = zr.left + zr.width / 2;
    stickOriginY = zr.top + zr.height / 2;
    stickBase.classList.add('tc-active');
    onSteerMove(e); // apply the initial deflection right away
    e.preventDefault();
  };

  const onSteerMove = (e: PointerEvent): void => {
    if (e.pointerId !== stickPointerId) return;
    const dx = e.clientX - stickOriginX;
    const dy = e.clientY - stickOriginY;
    const dist = Math.hypot(dx, dy);
    const scale = dist > STICK_RADIUS_PX ? STICK_RADIUS_PX / dist : 1;
    stick.x = (dx * scale) / STICK_RADIUS_PX;
    stick.y = (dy * scale) / STICK_RADIUS_PX;
    stickNub.style.transform = `translate(${dx * scale}px, ${dy * scale}px)`;
  };

  const onSteerUp = (e: PointerEvent): void => {
    if (e.pointerId !== stickPointerId) return;
    resetStick();
  };

  steerZone.addEventListener('pointerdown', onSteerDown);
  steerZone.addEventListener('pointermove', onSteerMove);
  steerZone.addEventListener('pointerup', onSteerUp);
  steerZone.addEventListener('pointercancel', onSteerUp);
  steerZone.addEventListener('lostpointercapture', onSteerUp);

  // ---------- Fire button (hold = shoot, vertical drag = range) ----------
  let firePointerId: number | null = null;
  let fireStartY = 0;
  let rangeStep: -1 | 0 | 1 = 0; // +1 = MoreRange, -1 = LessRange

  const resetFire = (): void => {
    firePointerId = null;
    rangeStep = 0;
    fireBtn.classList.remove('tc-held', 'tc-range-up', 'tc-range-down');
  };

  const onFireDown = (e: PointerEvent): void => {
    if (firePointerId !== null) return;
    firePointerId = e.pointerId;
    capturePointer(fireBtn, e.pointerId);
    fireStartY = e.clientY;
    rangeStep = 0;
    fireBtn.classList.add('tc-held');
    e.preventDefault();
  };

  const onFireMove = (e: PointerEvent): void => {
    if (e.pointerId !== firePointerId) return;
    const rise = fireStartY - e.clientY; // + = dragged up
    rangeStep = rise > RANGE_DRAG_PX ? 1 : rise < -RANGE_DRAG_PX ? -1 : 0;
    fireBtn.classList.toggle('tc-range-up', rangeStep === 1);
    fireBtn.classList.toggle('tc-range-down', rangeStep === -1);
  };

  const onFireUp = (e: PointerEvent): void => {
    if (e.pointerId !== firePointerId) return;
    resetFire();
  };

  fireBtn.addEventListener('pointerdown', onFireDown);
  fireBtn.addEventListener('pointermove', onFireMove);
  fireBtn.addEventListener('pointerup', onFireUp);
  fireBtn.addEventListener('pointercancel', onFireUp);
  fireBtn.addEventListener('lostpointercapture', onFireUp);

  // ---------- Mine button (tap = one-frame pulse) ----------
  // The sim drops ONE mine per tick the DropMine bit is held
  // (sim/src/mines.ts tryDropMine runs every tick and decrements
  // tank.mines each time) — a held bit would dump the whole stock at
  // 50/s. So a tap arms a pulse that readKeys() emits exactly once.
  let minePulse = false;

  const onMineDown = (e: PointerEvent): void => {
    minePulse = true;
    mineBtn.classList.add('tc-held');
    e.preventDefault();
  };
  const onMineUp = (): void => {
    mineBtn.classList.remove('tc-held');
  };

  mineBtn.addEventListener('pointerdown', onMineDown);
  mineBtn.addEventListener('pointerup', onMineUp);
  mineBtn.addEventListener('pointercancel', onMineUp);

  // ---------- Per-frame composition ----------
  let shownRange = ''; // last rendered range text (avoid DOM churn per frame)
  let wasBraking = false; // drive() hysteresis: sticky pull-back brake
  let wasThrusting = false; // drive() hysteresis: aim/drive ring boundary

  const source: InputSource = {
    readKeys(): number {
      const tank = ctx.myTank();
      let bits = 0;

      if (tank) {
        // Full drive law (steering.ts drive): release = brake, pull-back
        // while moving = STICKY brake (hysteresis — finger drift can't
        // drop it), then U-turn once stopped, otherwise steer with a
        // narrow thrust cone.
        const d = drive(stick, tank.dir, tank.speed, wasBraking, undefined, wasThrusting);
        bits |= d.bits;
        wasBraking = d.braking;
        wasThrusting = d.thrusting;
        if (stickPointerId !== null) {
          // BRAKE visual keys off the law's actual state, plus the
          // stopped-pivot case (red also reads as "turning around" per
          // the control-panel design).
          const stoppedPivot =
            tank.speed <= 0.5 &&
            Math.hypot(stick.x, stick.y) > 0.15 &&
            Math.abs(shortestDelta(tank.dir, stickToDir(stick))) >= 88;
          stickBase.classList.toggle('tc-reversing', d.braking || stoppedPivot);
        }
      } else {
        wasBraking = false;
        wasThrusting = false;
      }

      if (firePointerId !== null) {
        bits |= TankKey.Shoot;
        if (rangeStep === 1) bits |= TankKey.MoreRange;
        else if (rangeStep === -1) bits |= TankKey.LessRange;
      }

      if (minePulse) {
        bits |= TankKey.DropMine;
        minePulse = false; // exactly one readKeys() call per tap
      }

      // Range readout on the fire button (ClientTank.sightLen, half-tiles —
      // shown in tiles with one decimal to match the HUD stat strip).
      const rangeText = tank ? (tank.sightLen / 2).toFixed(1) : '';
      if (rangeText !== shownRange) {
        shownRange = rangeText;
        rangeLabel.textContent = rangeText;
      }

      return bits;
    },

    consumeBuild(): BuildCommand | null {
      // Build targeting goes through the armed-action path in main.ts
      // (HUD arms, stage tap targets) — touch controls issue none.
      return null;
    },

    dispose(): void {
      steerZone.removeEventListener('pointerdown', onSteerDown);
      steerZone.removeEventListener('pointermove', onSteerMove);
      steerZone.removeEventListener('pointerup', onSteerUp);
      steerZone.removeEventListener('pointercancel', onSteerUp);
      steerZone.removeEventListener('lostpointercapture', onSteerUp);
      fireBtn.removeEventListener('pointerdown', onFireDown);
      fireBtn.removeEventListener('pointermove', onFireMove);
      fireBtn.removeEventListener('pointerup', onFireUp);
      fireBtn.removeEventListener('pointercancel', onFireUp);
      fireBtn.removeEventListener('lostpointercapture', onFireUp);
      mineBtn.removeEventListener('pointerdown', onMineDown);
      mineBtn.removeEventListener('pointerup', onMineUp);
      mineBtn.removeEventListener('pointercancel', onMineUp);
      resetStick();
      resetFire();
      layer.remove();
    },
  };

  ctx.setInputSource(source);

  return { dispose: () => source.dispose(), element: layer };
}
